import React, { createContext, useState, useContext, useEffect } from 'react';
import i18n from 'i18next';

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [chatbot, setChatbot] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load chatbot from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('customerToken');
    const storedChatbot = localStorage.getItem('customerChatbot');
    
    if (storedToken && storedChatbot) {
      setToken(storedToken);
      setChatbot(JSON.parse(storedChatbot));
      // Verify token is still valid
      verifyToken(storedToken);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (tokenToVerify) => {
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${tokenToVerify}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.chatbot) {
          setChatbot(data.chatbot);
          setToken(tokenToVerify);
        } else {
          // Token is for admin, not customer
          logout();
        }
      } else {
        // Token invalid, clear storage
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch('/api/auth/customer-login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        setChatbot(data.chatbot);
        setToken(data.token);
        localStorage.setItem('customerToken', data.token);
        localStorage.setItem('customerChatbot', JSON.stringify(data.chatbot));
        return { success: true };
      } else {
return { success: false, error: data.error || i18n.t('auth.loginFailed') };
    }
    } catch (error) {
      return { success: false, error: i18n.t('auth.networkError') };
    }
  };

  const logout = () => {
    setChatbot(null);
    setToken(null);
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerChatbot');
  };

  const getAuthHeaders = () => {
    if (!token) return {};
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  return (
    <CustomerAuthContext.Provider value={{
      chatbot,
      token,
      loading,
      login,
      logout,
      isAuthenticated: !!chatbot,
      getAuthHeaders
    }}>
      {children}
    </CustomerAuthContext.Provider>
  );
};
